import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, FormControl, Select, MenuItem,
  CircularProgress, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, TextField,
} from '@mui/material';
import {
  ArrowBack,
  Download as DownloadIcon,
  People as PeopleIcon,
  School as TraineeIcon,
  SupervisorAccount as StaffIcon,
  Category as TypesIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
import styles from './TrainingReport.module.css';

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, iconBg, valueColor = '#003063' }) => (
  <Paper elevation={0} className={styles.statCard}>
    <Box className={styles.statIcon} sx={{ bgcolor: iconBg }}>{icon}</Box>
    <Box>
      <Typography className={styles.statLabel}>{label}</Typography>
      <Typography className={styles.statValue} sx={{ color: valueColor }}>{value}</Typography>
    </Box>
  </Paper>
);

// ─── Event Select renderer ───────────────────────────────────────────────────
// Shared between the Attendee and Staff event pickers. Each picker gets its
// own dropdown state ("dd"/"setDD") and selection handlers so they search and
// open independently.
function renderEventSelect({
  label,
  placeholder,
  selectedObj,
  dd,
  setDD,
  onPick,
  onClear,
  fmtDate,
  stylesEventSelect,
  stylesFilterGroup,
  stylesFilterLabel,
}) {
  const selectedId = selectedObj?.EventsID || '';
  return (
    <Box className={stylesFilterGroup} key={label}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography className={stylesFilterLabel}>{label}</Typography>
        {onClear && (
          <Button size="small" onClick={onClear} sx={{ fontSize: 11, minWidth: 0 }}>
            Clear
          </Button>
        )}
      </Box>
      <FormControl size="small" className={stylesEventSelect}>
        <Select
          open={dd.open}
          onOpen={() => setDD(prev => ({ ...prev, open: true }))}
          onClose={() => setDD(prev => ({ ...prev, open: false, search: '' }))}
          displayEmpty
          value={selectedId}
          onChange={() => {/* selection handled per-item */}}
          renderValue={() =>
            selectedObj ? selectedObj.BP_Title : <em>{placeholder}</em>
          }
          MenuProps={{
            autoFocus: false,
            disableAutoFocusItem: true,
            PaperProps: { sx: { maxHeight: 420, minWidth: 480 } },
          }}
        >
          {/* Sticky search field at the top of the dropdown */}
          <Box
            sx={{ p: 1, position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 2, borderBottom: '1px solid #eee' }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Search events…"
              value={dd.search}
              onChange={(e) => setDD(prev => ({ ...prev, search: e.target.value }))}
              // Stop the Select from hijacking keystrokes (its built-in
              // type-ahead-to-select moves focus and closes the menu).
              onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation(); }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              InputProps={{
                endAdornment: dd.loading
                  ? <CircularProgress color="inherit" size={16} />
                  : null,
              }}
            />
          </Box>

          {/* Keep the currently-selected event in the value list even when not in current results */}
          {selectedObj
            && !dd.options.some((o) => Number(o.EventsID) === Number(selectedObj.EventsID)) && (
              <MenuItem
                key={`hidden-${selectedObj.EventsID}`}
                value={selectedObj.EventsID}
                sx={{ display: 'none' }}
              />
            )}

          {/* Empty state */}
          {!dd.loading && dd.options.length === 0 && (
            <Box sx={{ p: 2, color: 'text.secondary', fontSize: 14 }}>
              {dd.debounced ? 'No matching events' : 'Type to search…'}
            </Box>
          )}

          {/* Result rows */}
          {dd.options.map((ev) => (
            <MenuItem
              key={ev.EventsID}
              value={ev.EventsID}
              onClick={() => onPick(ev)}
              sx={{ whiteSpace: 'normal' }}
            >
              {ev.BP_Title}
              {ev.BP_StartDate && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({fmtDate(ev.BP_StartDate)})
                </Typography>
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const TrainingReport = () => {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Two event selectors: an Attendee event (primary, supplies trainees) and an
  // optional Staff event (when staff registered under a separate event).
  const [selectedEventObj, setSelectedEventObj] = useState(null);
  const [selectedStaffEventObj, setSelectedStaffEventObj] = useState(null);
  const selectedEvent = selectedEventObj?.EventsID || '';
  const selectedStaffEvent = selectedStaffEventObj?.EventsID || '';

  // Each dropdown has its own option list, search state, and open flag so they
  // can be searched/opened independently.
  const [attendeeDD, setAttendeeDD] = useState({
    options: [], search: '', debounced: '', loading: false, open: false,
  });
  const [staffDD, setStaffDD] = useState({
    options: [], search: '', debounced: '', loading: false, open: false,
  });

  const [preview,         setPreview]         = useState(null);
  const [isLoadingPreview,setIsLoadingPreview]= useState(false);
  const [isDownloading,   setIsDownloading]   = useState(false);

  // Debounce the search inputs
  useEffect(() => {
    const t = setTimeout(
      () => setAttendeeDD(prev => ({ ...prev, debounced: prev.search.trim() })),
      300
    );
    return () => clearTimeout(t);
  }, [attendeeDD.search]);

  useEffect(() => {
    const t = setTimeout(
      () => setStaffDD(prev => ({ ...prev, debounced: prev.search.trim() })),
      300
    );
    return () => clearTimeout(t);
  }, [staffDD.search]);

  // Lazy-fetch events when each dropdown is open. Only the matching slice (max 50).
  const fetchEventOptions = useCallback(
    async (search) => {
      const res = await axiosPrivate.get('/api/reports/events', {
        params: { search: search || undefined, limit: 50 },
      });
      return res.data || [];
    },
    [axiosPrivate]
  );

  useEffect(() => {
    if (!attendeeDD.open) return;
    let cancelled = false;
    setAttendeeDD(prev => ({ ...prev, loading: true }));
    fetchEventOptions(attendeeDD.debounced)
      .then(data => { if (!cancelled) setAttendeeDD(prev => ({ ...prev, options: data })); })
      .catch(() => {
        if (!cancelled) {
          setAttendeeDD(prev => ({ ...prev, options: [] }));
          enqueueSnackbar('Failed to load events', { variant: 'error' });
        }
      })
      .finally(() => { if (!cancelled) setAttendeeDD(prev => ({ ...prev, loading: false })); });
    return () => { cancelled = true; };
  }, [attendeeDD.open, attendeeDD.debounced, fetchEventOptions, enqueueSnackbar]);

  useEffect(() => {
    if (!staffDD.open) return;
    let cancelled = false;
    setStaffDD(prev => ({ ...prev, loading: true }));
    fetchEventOptions(staffDD.debounced)
      .then(data => { if (!cancelled) setStaffDD(prev => ({ ...prev, options: data })); })
      .catch(() => {
        if (!cancelled) {
          setStaffDD(prev => ({ ...prev, options: [] }));
          enqueueSnackbar('Failed to load events', { variant: 'error' });
        }
      })
      .finally(() => { if (!cancelled) setStaffDD(prev => ({ ...prev, loading: false })); });
    return () => { cancelled = true; };
  }, [staffDD.open, staffDD.debounced, fetchEventOptions, enqueueSnackbar]);

  // Auto-preview when either event changes
  const fetchPreview = useCallback(async (eventId, staffEventId) => {
    if (!eventId) { setPreview(null); return; }
    setIsLoadingPreview(true);
    setPreview(null);
    try {
      const params = { format: 'json' };
      if (staffEventId && Number(staffEventId) !== Number(eventId)) {
        params.staffEventId = staffEventId;
      }
      const res = await axiosPrivate.get(`/api/reports/training/${eventId}`, { params });
      setPreview(res.data);
    } catch {
      enqueueSnackbar('Failed to load preview.', { variant: 'error' });
    } finally {
      setIsLoadingPreview(false);
    }
  }, [axiosPrivate, enqueueSnackbar]);

  const handleAttendeeEventChange = (event) => {
    setSelectedEventObj(event);
    setAttendeeDD(prev => ({ ...prev, open: false, search: '' }));
    fetchPreview(event?.EventsID || '', selectedStaffEvent);
  };

  const handleStaffEventChange = (event) => {
    setSelectedStaffEventObj(event);
    setStaffDD(prev => ({ ...prev, open: false, search: '' }));
    fetchPreview(selectedEvent, event?.EventsID || '');
  };

  const clearStaffEvent = () => {
    setSelectedStaffEventObj(null);
    fetchPreview(selectedEvent, '');
  };

  const handleDownload = async () => {
    if (!selectedEvent) return;
    setIsDownloading(true);
    try {
      const params = { format: 'excel' };
      if (selectedStaffEvent && Number(selectedStaffEvent) !== Number(selectedEvent)) {
        params.staffEventId = selectedStaffEvent;
      }
      const res = await axiosPrivate.get(
        `/api/reports/training/${selectedEvent}`,
        { params, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a');
      a.href     = url;
      const eventTitle = preview?.event?.title || `Event_${selectedEvent}`;
      const staffTitle = preview?.staffEvent?.title;
      const safe = (s) => (s || '').replace(/[^a-z0-9]/gi, '_');
      a.download = staffTitle
        ? `Training_Report_${safe(eventTitle)}__${safe(staffTitle)}.xlsx`
        : `Training_Report_${safe(eventTitle)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar('Export failed. Please try again.', { variant: 'error' });
    } finally {
      setIsDownloading(false);
    }
  };

  const fmtDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <main>
      {/* Header */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12">
              <h1 className="text-uppercase">Training Report</h1>
              <p>Per-event Excel export with attendee breakdown by registration type — staff separated from trainees.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="full-row white">
        <Box className={styles.container}>

          {/* Back */}
          <Box className={styles.backRow}>
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/admin/reporting')}>
              Back to Reports
            </Button>
          </Box>

          {/* Filter panel */}
          <Paper className={styles.filterPanel} elevation={0}>
            <Box className={styles.filterRow}>

              {renderEventSelect({
                label: 'Attendee Event',
                placeholder: '— Select attendee event —',
                selectedObj: selectedEventObj,
                dd: attendeeDD,
                setDD: setAttendeeDD,
                onPick: handleAttendeeEventChange,
                onClear: null,
                fmtDate,
                stylesEventSelect: styles.eventSelect,
                stylesFilterGroup: styles.filterGroup,
                stylesFilterLabel: styles.filterLabel,
              })}

              {renderEventSelect({
                label: 'Staff Event (optional)',
                placeholder: '— Same as attendee event —',
                selectedObj: selectedStaffEventObj,
                dd: staffDD,
                setDD: setStaffDD,
                onPick: handleStaffEventChange,
                onClear: selectedStaffEventObj ? clearStaffEvent : null,
                fmtDate,
                stylesEventSelect: styles.eventSelect,
                stylesFilterGroup: styles.filterGroup,
                stylesFilterLabel: styles.filterLabel,
              })}

              <Box className={styles.downloadBox}>
                <Button
                  variant="contained"
                  startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                  onClick={handleDownload}
                  disabled={!selectedEvent || isDownloading || isLoadingPreview}
                  className={styles.downloadBtn}
                >
                  {isDownloading ? 'Generating…' : 'Download Excel'}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Combined Overview sheet (Trainees · Summary · Staff) + one tab per class
                </Typography>
              </Box>

            </Box>
          </Paper>

          {/* Loading preview */}
          {isLoadingPreview && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#003063' }} />
            </Box>
          )}

          {/* Preview */}
          {!isLoadingPreview && preview && (
            <>
              {/* Event title */}
              <Box className={styles.previewHeader}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#003063' }}>
                  {preview.event.title}
                </Typography>
                {preview.event.startDate && (
                  <Typography variant="body2" color="text.secondary">
                    Attendees · {fmtDate(preview.event.startDate)}
                  </Typography>
                )}
                {preview.staffEvent && (
                  <Typography variant="body2" color="text.secondary">
                    Staff from: {preview.staffEvent.title}
                    {preview.staffEvent.startDate ? ` · ${fmtDate(preview.staffEvent.startDate)}` : ''}
                  </Typography>
                )}
              </Box>

              {/* Stat cards */}
              <Box className={styles.statsRow}>
                <StatCard
                  icon={<PeopleIcon sx={{ color: '#003063' }} />}
                  label="TOTAL ATTENDEES"
                  value={preview.totalAttendees}
                  iconBg="#e8f0fe"
                  valueColor="#003063"
                />
                <StatCard
                  icon={<StaffIcon sx={{ color: '#1565c0' }} />}
                  label="STAFF"
                  value={preview.staffCount}
                  iconBg="#e3f2fd"
                  valueColor="#1565c0"
                />
                <StatCard
                  icon={<TraineeIcon sx={{ color: '#2e7d32' }} />}
                  label="TRAINEES"
                  value={preview.traineeCount}
                  iconBg="#e8f5e9"
                  valueColor="#2e7d32"
                />
                <StatCard
                  icon={<TypesIcon sx={{ color: '#e65100' }} />}
                  label="CLASSES / TYPES"
                  value={preview.typeCount}
                  iconBg="#fff3e0"
                  valueColor="#e65100"
                />
              </Box>

              {/* Summary table */}
              <Box className={styles.sectionHeader}>
                <Typography className={styles.sectionTitle}>BREAKDOWN BY REGISTRATION TYPE</Typography>
              </Box>

              <Paper className={styles.tablePaper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow className={styles.tableHead}>
                      <TableCell>Registration Type</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">% of Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.summary.map((row, i) => (
                      <TableRow key={row.type} className={i % 2 === 1 ? styles.stripedRow : ''}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{row.type}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.category}
                            size="small"
                            sx={{
                              bgcolor: row.category === 'Staff' ? '#e3f2fd' : '#e8f5e9',
                              color:   row.category === 'Staff' ? '#1565c0' : '#2e7d32',
                              fontWeight: 700,
                              fontSize: 11,
                              border: 'none',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{row.count}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {preview.totalAttendees > 0
                              ? `${((row.count / preview.totalAttendees) * 100).toFixed(1)}%`
                              : '0%'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals */}
                    <TableRow className={styles.totalsRow}>
                      <TableCell colSpan={2}>
                        <Typography variant="body2" fontWeight={700} sx={{ color: '#003063' }}>TOTAL</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} sx={{ color: '#003063' }}>{preview.totalAttendees}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} sx={{ color: '#003063' }}>100%</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>

              {/* Download CTA */}
              <Box sx={{ mt: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={isDownloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className={styles.downloadBtn}
                >
                  {isDownloading ? 'Generating Excel…' : 'Download Excel Report'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Includes: Overview (Trainees · Summary · Staff) · {preview.typeCount} class tab{preview.typeCount !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </>
          )}

          {/* Empty state */}
          {!isLoadingPreview && !preview && selectedEvent && (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography color="text.secondary">No attendees found for this event.</Typography>
            </Paper>
          )}

        </Box>
      </div>
    </main>
  );
};

export default TrainingReport;
